import { Button, Grid, Link, OutlinedInput, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { Box } from "@mui/system";
import { LoadingButton } from "@mui/lab";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { apiConstants, postMultipart, getGeneric } from "../../common/constants";
import { formatDate4 } from "../../common/util";
import { FormProvider, RHFDateField, RHFSelectField, RHFTextField } from "../../common/hook-form";
import ExtraDocsEditor, { collectExtraDocs, categoryLabel } from "./ExtraDocsEditor";
import { getEditWindow, getEarliestEditableDate, formatCutoff } from "../AppointmentDeliveriesV2/shared/useAbdSettings";
import { ABD_EDIT_REASON_CATEGORY } from "../AppointmentDeliveriesV2/shared/constants";

const TIME_SLOTS = [
    { value: '8-10AM', label: '8-10 AM', startTime: '08:00:00', endTime: '10:00:00' },
    { value: '10-12PM', label: '10-12 PM', startTime: '10:00:00', endTime: '12:00:00' },
    { value: '12-2PM', label: '12-2 PM', startTime: '12:00:00', endTime: '14:00:00' },
];

// Every field of the form. When all are empty, the popup only saves documents against the
// AWB (/additional_documents/upload): no appointment is created, the shipment stays in
// Needs Scheduling. Filling any field means scheduling, and the full validation applies.
const FORM_FIELDS = ['appointmentDate', 'timeSlot', 'appointment_del_po', 'appointment_del_po_expiry',
    'appointment_del_remarks', 'appointment_del_alt_contact_1', 'appointment_del_alt_contact_2'];
const isBlank = (v) => v == null || v === '' || (v instanceof Date && isNaN(v));
const isDocsOnly = (values) => FORM_FIELDS.every((f) => isBlank(values?.[f]));

// Pre-appointment docs live on order.additionalDocuments under these keys.
const ADDITIONAL_DOC_KEYS = { INVOICE: 'invoiceS3Key', EWAY_BILL: 'ewayBillS3Key', PO_COPY: 'poCopyS3Key' };

// Schedule a B2B delivery appointment. Documents (3 fixed + categorized extras) are
// part of the appointment and are submitted together via /appointment_delivery.
// With the form left empty, it saves the documents only (see isDocsOnly).
function AppointmentDelivery(props) {
    const [isSubmitting, setIsSubmitting] = useState(false)
    const { enqueueSnackbar } = useSnackbar();
    const { rowData, setDelConfirm, setReload, isEditMode = false, abdSettings } = props;

    const phoneRegex = /^[0-9]{10}$/;
    const AppointmentSchema = Yup.object().shape({
        appointmentDate: Yup.date().typeError('Delivery date is required').required('Delivery date is required'),
        timeSlot: Yup.string().required('Time slot is required'),
        appointment_del_po: Yup.string().required('PO Detail is required'),
        appointment_del_po_expiry: Yup.date().typeError('PO Expiry Date is required').required('PO Expiry Date is required'),
        appointment_del_alt_contact_1: Yup.string().nullable().notRequired().test('phone1', 'Must be 10 digits', v => !v || phoneRegex.test(v)),
        appointment_del_alt_contact_2: Yup.string().nullable().notRequired().test('phone2', 'Must be 10 digits', v => !v || phoneRegex.test(v)),
        reasonCode: isEditMode
            ? Yup.string().required('Reason for edit is required')
            : Yup.string().nullable().notRequired(),
        reasonRemarks: isEditMode
            ? Yup.string().trim().required('Edit remarks are required').max(500, 'Max 500 characters')
            : Yup.string().nullable().notRequired(),
    });

    const methods = useForm({
        // Docs-only save has no form fields to validate; otherwise the appointment rules apply.
        resolver: (values, context, options) => isDocsOnly(values) && !isEditMode
            ? { values, errors: {} }
            : yupResolver(AppointmentSchema)(values, context, options),
        defaultValues: {
            timeSlot: '',
            appointmentDate: null,
            appointment_del_po: '',
            appointment_del_po_expiry: null,
            appointment_del_remarks: '',
            appointment_del_alt_contact_1: '',
            appointment_del_alt_contact_2: '',
            reasonCode: '',
            reasonRemarks: '',
        }
    });
    const { handleSubmit, reset, setValue, watch } = methods;
    const docsOnly = isDocsOnly(watch()) && !isEditMode;
    const [files, setFiles] = useState({ invoice: null, eway_bill: null, po_copy: null });
    const [extraDocRows, setExtraDocRows] = useState([]);
    const [reasons, setReasons] = useState([]);
    const [initialValues, setInitialValues] = useState(null);

    useEffect(() => {
        if (!isEditMode) return;
        let mounted = true;
        getGeneric(apiConstants.REASON_MASTER, { category: ABD_EDIT_REASON_CATEGORY, activeOnly: true })
            .then(res => {
                const list = Array.isArray(res?.data) ? res.data : [];
                if (mounted) setReasons(list);
            })
            .catch(() => {
                if (mounted) enqueueSnackbar("Could not load reasons for edit", { variant: "error" });
            });
        return () => { mounted = false; };
    }, [isEditMode, enqueueSnackbar]);

    useEffect(() => {
        const active = rowData?.appointmentDelivery && rowData?.appointmentDeliveryData
            ? rowData.appointmentDeliveryData
            : null;
        const lastHistory = !active && Array.isArray(rowData?.appointmentDeliveryHistory) && rowData.appointmentDeliveryHistory.length
            ? rowData.appointmentDeliveryHistory[rowData.appointmentDeliveryHistory.length - 1]
            : null;
        const carry = active || lastHistory;
        // Editor holds NEW docs only; already-saved extras are shown read-only (append-only).
        setExtraDocRows([]);
        if (!carry) return;

        let timeSlotValue = '';
        if (active?.appointment_del_start) {
            const startHour = dayjs(active.appointment_del_start).hour();
            if (startHour === 8) timeSlotValue = '8-10AM';
            else if (startHour === 10) timeSlotValue = '10-12PM';
            else if (startHour === 12) timeSlotValue = '12-2PM';
            setValue('appointmentDate', dayjs(active.appointment_del_start).toDate());
            setValue('timeSlot', timeSlotValue);
        }
        if (carry.appointment_del_po) {
            setValue('appointment_del_po', carry.appointment_del_po);
        }
        if (carry.appointment_del_po_expiry) {
            setValue('appointment_del_po_expiry', dayjs(carry.appointment_del_po_expiry).toDate());
        }
        if (carry.appointment_del_alt_contact_1) {
            setValue('appointment_del_alt_contact_1', carry.appointment_del_alt_contact_1);
        }
        if (carry.appointment_del_alt_contact_2) {
            setValue('appointment_del_alt_contact_2', carry.appointment_del_alt_contact_2);
        }
        if (active?.appointment_del_remarks) {
            setValue('appointment_del_remarks', active.appointment_del_remarks);
        }

        setInitialValues({
            appointmentDate: active?.appointment_del_start ? dayjs(active.appointment_del_start).format('YYYY-MM-DD') : '',
            timeSlot: timeSlotValue,
            appointment_del_po: carry.appointment_del_po || '',
            appointment_del_po_expiry: carry.appointment_del_po_expiry ? dayjs(carry.appointment_del_po_expiry).format('YYYY-MM-DD') : '',
            appointment_del_remarks: active?.appointment_del_remarks || '',
            appointment_del_alt_contact_1: carry.appointment_del_alt_contact_1 || '',
            appointment_del_alt_contact_2: carry.appointment_del_alt_contact_2 || '',
        });
    }, [rowData, setValue]);

    const formValues = watch();
    const hasFormChanged = () => {
        if (!isEditMode) return true;
        if (files.invoice || files.eway_bill || files.po_copy || extraDocRows.length > 0) return true;
        if (!initialValues) return false;
        const curDate = formValues.appointmentDate ? dayjs(formValues.appointmentDate).format('YYYY-MM-DD') : '';
        const curExpiry = formValues.appointment_del_po_expiry ? dayjs(formValues.appointment_del_po_expiry).format('YYYY-MM-DD') : '';
        if (curDate !== initialValues.appointmentDate) return true;
        if ((formValues.timeSlot || '') !== initialValues.timeSlot) return true;
        if ((formValues.appointment_del_po || '') !== initialValues.appointment_del_po) return true;
        if (curExpiry !== initialValues.appointment_del_po_expiry) return true;
        if ((formValues.appointment_del_remarks || '') !== initialValues.appointment_del_remarks) return true;
        if ((formValues.appointment_del_alt_contact_1 || '') !== initialValues.appointment_del_alt_contact_1) return true;
        if ((formValues.appointment_del_alt_contact_2 || '') !== initialValues.appointment_del_alt_contact_2) return true;
        return false;
    };
    const isChanged = hasFormChanged();

    const onSubmit = async (data) => {
        setIsSubmitting(true)
        try {
            const slot = TIME_SLOTS.find(s => s.value === data.timeSlot);
            const startTime = slot?.startTime || null;
            const endTime = slot?.endTime || null;

            const fd = new FormData();
            const { extraDocs, error } = collectExtraDocs(extraDocRows, fd);
            if (error) {
                enqueueSnackbar(error, { variant: "error" });
                setIsSubmitting(false);
                return;
            }

            if (!isEditMode && isDocsOnly(data)) {
                if (!files.invoice && !files.eway_bill && !files.po_copy && extraDocs.length === 0) {
                    enqueueSnackbar("Fill the appointment details, or add at least one document.", { variant: "warning" });
                    setIsSubmitting(false);
                    return;
                }
                // The upload endpoint replaces the extraDocs list, so send the saved ones along with the new.
                const docsPayload = { awbNumber: rowData?.awb_number, extraDocs: [...additionalExtra, ...extraDocs] };
                fd.append('data', new Blob([JSON.stringify(docsPayload)], { type: 'application/json' }));
                if (files.invoice) fd.append('invoice', files.invoice);
                if (files.eway_bill) fd.append('eway_bill', files.eway_bill);
                if (files.po_copy) fd.append('po_copy', files.po_copy);
                await postMultipart(apiConstants.ADDITIONAL_DOCUMENTS_UPLOAD, fd);
                setDelConfirm(false)
                enqueueSnackbar("Documents saved", { variant: "success" });
                setReload(reload => reload + 1)
                reset()
                setIsSubmitting(false)
                return;
            }

            if (isEditMode) {
                if (!isChanged) {
                    enqueueSnackbar("No changes detected. Modify at least one field to save.", { variant: "warning" });
                    setIsSubmitting(false);
                    return;
                }
                const histCount = rowData?.appointmentDeliveryHistoryCount ?? rowData?.appointmentDeliveryHistory?.length ?? 0;
                const editsCount = rowData?.appointmentDeliveryData?.appointment_del_edits?.length || 0;
                const payload = {
                    "awbNumber": rowData?.awb_number,
                    "appointmentDeliveryDate": formatDate4(data.appointmentDate, 'yyyy MMM, dd'),
                    "reasonCode": data.reasonCode,
                    "reasonRemarks": data.reasonRemarks || null,
                    "appointmentNumber": histCount + 1,
                    "editCount": editsCount,
                    "data": {
                        "appointment_del_start": data.appointmentDate && startTime
                            ? `${formatDate4(data.appointmentDate, 'yyyy MMM, dd')}T${startTime}`
                            : null,
                        "appointment_del_end": data.appointmentDate && endTime
                            ? `${formatDate4(data.appointmentDate, 'yyyy MMM, dd')}T${endTime}`
                            : null,
                        "appointment_del_po": data.appointment_del_po,
                        "appointment_del_po_expiry": formatDate4(data.appointment_del_po_expiry, 'yyyy MMM, dd'),
                        "appointment_del_remarks": data.appointment_del_remarks,
                        "appointment_del_alt_contact_1": data.appointment_del_alt_contact_1 || null,
                        "appointment_del_alt_contact_2": data.appointment_del_alt_contact_2 || null,
                        "appointment_del_extra_docs": extraDocs,
                    }
                };
                fd.append('data', new Blob([JSON.stringify(payload)], { type: 'application/json' }));
                if (files.invoice) fd.append('invoice', files.invoice);
                if (files.eway_bill) fd.append('eway_bill', files.eway_bill);
                if (files.po_copy) fd.append('po_copy', files.po_copy);
                await postMultipart(apiConstants.APPOINTMENT_DELIVERY_EDIT, fd);
                setDelConfirm(false);
                enqueueSnackbar("Appointment updated successfully", { variant: "success" });
                setReload(reload => reload + 1);
                reset();
                setIsSubmitting(false);
                return;
            }

            const payload = {
                "awbNumber": rowData?.awb_number,
                "appointmentDeliveryDate": formatDate4(data.appointmentDate, 'yyyy MMM, dd'),
                "data": {
                    "appointment_del_start": data.appointmentDate && startTime
                        ? `${formatDate4(data.appointmentDate, 'yyyy MMM, dd')}T${startTime}`
                        : null,
                    "appointment_del_end": data.appointmentDate && endTime
                        ? `${formatDate4(data.appointmentDate, 'yyyy MMM, dd')}T${endTime}`
                        : null,
                    "appointment_del_po": data.appointment_del_po,
                    "appointment_del_po_expiry": formatDate4(data.appointment_del_po_expiry, 'yyyy MMM, dd'),
                    "appointment_del_remarks": data.appointment_del_remarks,
                    "appointment_del_alt_contact_1": data.appointment_del_alt_contact_1 || null,
                    "appointment_del_alt_contact_2": data.appointment_del_alt_contact_2 || null,
                    "appointment_del_extra_docs": [...existingExtra, ...extraDocs],
                }
            };
            fd.append('data', new Blob([JSON.stringify(payload)], { type: 'application/json' }));
            if (files.invoice) fd.append('invoice', files.invoice);
            if (files.eway_bill) fd.append('eway_bill', files.eway_bill);
            if (files.po_copy) fd.append('po_copy', files.po_copy);
            await postMultipart(apiConstants.APPOINTMENT_DELIVERY, fd);
            setDelConfirm(false)
            enqueueSnackbar("Delivery scheduled", { variant: "success" });
            setReload(reload => reload + 1)
            reset()
        }
        catch (err) {
            console.log(err)
            enqueueSnackbar(err?.response?.data?.message || "Something went wrong", { variant: "error" });
        }
        setIsSubmitting(false)
    }

    const getMinDate = () => {
        const tomorrow = dayjs().add(1, 'day').startOf('day');
        if (isEditMode) {
            return getEarliestEditableDate(abdSettings);
        }
        if (rowData?.edd) {
            const eddPlusOne = dayjs(rowData.edd).add(1, 'day').startOf('day');
            return eddPlusOne.isAfter(tomorrow) ? eddPlusOne.toDate() : tomorrow.toDate();
        }
        return tomorrow.toDate();
    };

    const timeSlots = TIME_SLOTS.map(s => ({ value: s.value, label: s.label }));

    const docs = [
        { key: 'invoice', label: 'Upload Invoice', keyField: 'appointment_del_invoice_s3_key', docType: 'INVOICE' },
        { key: 'eway_bill', label: 'Upload E-Way Bill', keyField: 'appointment_del_eway_bill_s3_key', docType: 'EWAY_BILL' },
        { key: 'po_copy', label: 'Upload PO Copy', keyField: 'appointment_del_po_copy_s3_key', docType: 'PO_COPY' },
    ];

    const activeAppt = rowData?.appointmentDelivery && rowData?.appointmentDeliveryData
        ? rowData.appointmentDeliveryData
        : null;
    const lastClosed = (rowData?.appointmentDeliveryHistory || []).slice(-1)[0];
    const lastClosedIndex = (rowData?.appointmentDeliveryHistory || []).length - 1;
    const existingExtra = (activeAppt || lastClosed)?.appointment_del_extra_docs || [];
    const additional = rowData?.additionalDocuments;
    const additionalExtra = additional?.extraDocs || [];

    const fetchDocUrl = async (docType, historyIndex) => {
        try {
            const params = { awbNumber: rowData?.awb_number, docType };
            if (historyIndex != null && historyIndex >= 0) params.historyIndex = historyIndex;
            const res = await getGeneric(apiConstants.APPOINTMENT_DELIVERY_DOC_URL, params);
            const url = res?.data?.responseObj?.url || res?.data?.url;
            if (url) window.open(url, '_blank');
            else enqueueSnackbar("Document URL missing", { variant: "warning" });
        } catch (err) {
            enqueueSnackbar(err?.response?.data?.message || "Failed to fetch document", { variant: "error" });
        }
    };

    const fetchAdditionalDocUrl = async (params) => {
        try {
            const res = await getGeneric(apiConstants.ADDITIONAL_DOCUMENTS_DOC_URL, { awbNumber: rowData?.awb_number, ...params });
            const url = res?.data?.responseObj?.url || res?.data?.url;
            if (url) window.open(url, '_blank');
            else enqueueSnackbar("Document URL missing", { variant: "warning" });
        } catch (err) {
            enqueueSnackbar(err?.response?.data?.message || "Failed to fetch document", { variant: "error" });
        }
    };

    const extraDocsHistoryIndex = activeAppt ? null : lastClosedIndex;
    const fetchExtraDocUrl = async (doc) => {
        try {
            const params = { awbNumber: rowData?.awb_number, docId: doc?.docId };
            if (extraDocsHistoryIndex != null && extraDocsHistoryIndex >= 0) params.historyIndex = extraDocsHistoryIndex;
            const res = await getGeneric(apiConstants.APPOINTMENT_DELIVERY_DOC_URL, params);
            const url = res?.data?.responseObj?.url || res?.data?.url;
            if (url) window.open(url, '_blank');
            else enqueueSnackbar("Document URL missing", { variant: "warning" });
        } catch (err) {
            enqueueSnackbar(err?.response?.data?.message || "Failed to fetch document", { variant: "error" });
        }
    };

    const editWindow = isEditMode ? getEditWindow(rowData, abdSettings) : null;
    const cutoffNotice = editWindow?.until ? formatCutoff(editWindow.until) : '';

    return (
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            {isEditMode ? (
                <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f3e8ff', border: '1px solid #d8b4fe', borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{ color: '#6b21a8', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <InfoOutlinedIcon fontSize="small" />
                        Editing Active Appointment
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#581c87', display: 'block', mt: 0.5 }}>
                        Changes update the current appointment in place, without adding an extra attempt or creating a miss. A new date or slot also moves the EDD to the new appointment.
                        {cutoffNotice ? ` Editable until ${cutoffNotice}.` : ''}
                    </Typography>
                </Box>
            ) : (
                <Typography variant="body2" color={'error'}>
                    Note: If appointment is after 2 PM, please contact with your KAM
                </Typography>
            )}
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={6}>
                    <RHFDateField
                        minDate={getMinDate()}
                        name='appointmentDate'
                        label={rowData?.edd ? `Delivery Date (EDD : ${dayjs(rowData?.edd).format('DD MMM, YYYY')})` : 'Delivery Date'}
                    />
                </Grid>
                <Grid item xs={6}>
                    <RHFSelectField
                        width="100%"
                        name="timeSlot"
                        label="Select Time Slot"
                        input={<OutlinedInput label="Select Time Slot" />}
                        options={timeSlots}
                    />
                </Grid>
                {isEditMode && (
                    <>
                        <Grid item xs={6}>
                            <RHFSelectField
                                width="100%"
                                name="reasonCode"
                                label="Reason for Edit *"
                                input={<OutlinedInput label="Reason for Edit *" />}
                                options={reasons.map((r) => ({ value: r.code, label: r.label || r.name || r.code }))}
                            />
                            {reasons.length === 0 && (
                                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                                    No edit reasons configured in Reason Master.
                                </Typography>
                            )}
                        </Grid>
                        <Grid item xs={6}>
                            <RHFTextField
                                name="reasonRemarks"
                                label="Edit Remarks *"
                                color="secondary"
                                variant="outlined"
                                multiline
                            />
                        </Grid>
                    </>
                )}
                <Grid item xs={6}>
                    <RHFTextField name="appointment_del_po" label="PO" color="secondary" variant="outlined" />
                </Grid>
                <Grid item xs={6}>
                    <RHFDateField disablePast name='appointment_del_po_expiry' label='PO Expiry Date' />
                </Grid>
                <Grid item xs={6}>
                    <RHFTextField name="appointment_del_remarks" label={isEditMode ? 'Appointment Remarks' : 'Remarks'} color="secondary" variant="outlined" multiline />
                </Grid>
                <Grid item xs={6}>
                    <RHFTextField name="appointment_del_alt_contact_1" label="Alternate Receiver Contact 1" color="secondary" variant="outlined" />
                </Grid>
                <Grid item xs={6}>
                    <RHFTextField name="appointment_del_alt_contact_2" label="Alternate Receiver Contact 2" color="secondary" variant="outlined" />
                </Grid>
                <Grid item xs={12}>
                    <Grid container spacing={2}>
                        {docs.map(({ key, label, keyField, docType }) => {
                            const activeKey = activeAppt?.[keyField];
                            // Pre-appointment upload is newer than a closed appointment's copy.
                            const additionalKey = !activeKey && additional?.[ADDITIONAL_DOC_KEYS[docType]];
                            const closedKey = lastClosed?.[keyField];
                            const existingKey = activeKey || additionalKey || closedKey;
                            const viewHistoryIndex = activeKey ? null : lastClosedIndex;
                            const onViewExisting = additionalKey
                                ? () => fetchAdditionalDocUrl({ docType })
                                : () => fetchDocUrl(docType, viewHistoryIndex);
                            const selected = files[key];
                            return (
                                <Grid item xs={12} sm={4} key={key}>
                                    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, height: '100%' }}>
                                        <Typography variant="caption" display="block" sx={{ mb: 0.5, fontWeight: 500 }}>{label}</Typography>
                                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                            <Button variant="outlined" size="small" component="label">
                                                {selected || existingKey ? 'Replace' : 'Choose File'}
                                                <input type="file" hidden onChange={(e) => setFiles(prev => ({ ...prev, [key]: e.target.files?.[0] || null }))} />
                                            </Button>
                                            {!selected && existingKey && (
                                                <Link component="button" type="button" variant="caption" onClick={onViewExisting}>View</Link>
                                            )}
                                        </Box>
                                        {selected && (
                                            <Typography variant="caption" noWrap display="block" sx={{ mt: 0.5, overflow: 'hidden', textOverflow: 'ellipsis' }} title={selected.name}>{selected.name}</Typography>
                                        )}
                                    </Box>
                                </Grid>
                            );
                        })}
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    {(existingExtra.length > 0 || additionalExtra.length > 0) && (
                        <Box sx={{ mb: 1.5 }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
                                Existing additional documents
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                                {existingExtra.map((xd) => (
                                    <Button key={xd.docId} size="small" variant="text" onClick={() => fetchExtraDocUrl(xd)}>
                                        {xd.docLabel || categoryLabel(xd.docType)}
                                    </Button>
                                ))}
                                {additionalExtra.map((xd) => (
                                    <Button key={xd.docId} size="small" variant="text" onClick={() => fetchAdditionalDocUrl({ docId: xd.docId })}>
                                        {xd.docLabel || categoryLabel(xd.docType)}
                                    </Button>
                                ))}
                            </Box>
                        </Box>
                    )}
                    <ExtraDocsEditor rows={extraDocRows} setRows={setExtraDocRows} onView={fetchExtraDocUrl} existingCount={docsOnly ? additionalExtra.length : existingExtra.length} />
                </Grid>
            </Grid>
            <Grid xs={12} display="flex" flexDirection="column" alignItems="flex-end" sx={{ paddingTop: 2 }}>
                <Box display="flex" justifyContent="flex-end">
                    <Box sx={{ paddingTop: 1, marginRight: 1 }} >
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setDelConfirm(false)}
                        >Close</Button>
                    </Box>
                    <Box sx={{ paddingTop: 1 }} >
                        <LoadingButton
                            loading={isSubmitting}
                            variant="contained"
                            size="small"
                            type="submit"
                            disabled={isEditMode && !isChanged}
                        >{docsOnly ? 'Save Documents' : isEditMode ? 'Save Changes' : 'Schedule'}</LoadingButton>
                    </Box>
                </Box>
                {isEditMode && !isChanged && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                        Modify at least one field to save changes
                    </Typography>
                )}
            </Grid>
        </FormProvider>
    )
}

export default AppointmentDelivery;
